import ctl from '@netlify/classnames-template-literals'
import { LoaderCircleIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { JobiLoader } from './jobiloader'
import { SubtleLoader } from './subtle-loader'

export interface CardButtonProps {
	onClick?: () => void
	type?: 'committal' | 'neutral' | 'subtle' // | 'destructive'
	icon?: ReactNode
	title?: ReactNode
	hint?: ReactNode
	seq?: number
	unavailable?: boolean
	loading?: boolean
}

const parentClasses: Record<NonNullable<CardButtonProps['type']>, string> = {
	committal: ctl(' hover:ring-brand-500 hover:shadow-brand-500/30'),
	neutral: ctl(' hover:ring-grey-800 '),
	subtle: ctl(' hover:ring-grey-200 '),
}

const buttonClasses: Record<NonNullable<CardButtonProps['type']>, string> = {
	committal: ctl('bg-brand-600 [data-unavailable=false]:group-hover:bg-brand-500 text-white'),
	neutral: ctl('bg-grey-900 [data-unavailable=false]:group-hover:bg-grey-800 text-white'),
	subtle: ctl('border-t border-grey-100  text-grey-700'),
}

export function CardButton(
	{ onClick = () => {}, type = 'neutral', icon, title, hint, seq = 0, unavailable = false, loading = true }:
		CardButtonProps,
) {
	const { t } = useTranslation('common')
	return (
		<motion.div className="grid grid-rows-subgrid grid-cols-subgrid relative group">
			<motion.button
				data-unavailable={unavailable}
				className={ctl(`
					rounded-lg ring ring-grey-100 shadow
					grid grid-rows-[1fr_auto_auto]
					${unavailable ? 'cursor-not-allowed' : 'cursor-pointer'} 
					relative					
				`)}
				initial={{
					translateY: 10,
					opacity: 0,
				}}
				animate={{
					translateY: 0,
					opacity: unavailable ? 0.5 : 1,
					transition: {
						delay: seq * 0.1,
					},
				}}
				whileHover={unavailable
					? {}
					: {
						translateY: -3,
						rotate: 0.9,
					}}
				whileTap={unavailable ? {} : {
					translateY: 3,
					rotate: 0.9,
					scale: 0.98,
				}}
				type="button"
				onClick={onClick}
			>
				<div
					data-unavailable={unavailable}
					className={ctl(
						`absolute inset-0 rounded-lg transition-all [data-unavailable=false]:hover:shadow-lg 
					${parentClasses[type]}`,
					)}
				/>
				<div className="flex items-center justify-center px-2 pt-4">
					{icon}
				</div>

				<div className="flex flex-col gap-0.5 pb-4 px-6">
					<div className="text-lg font-medium text-grey-900">{title}</div>
					<div className="text-sm text-grey-700">{hint}</div>
				</div>

				<div
					className={ctl(`
					flex items-center justify-center py-3
					transition-all
					rounded-b-lg
					${buttonClasses[type]}
				`)}
				>
					{loading
						? (
							<SubtleLoader delay={0}>
								{t('cardButton.creating')}
							</SubtleLoader>
						)
						: <Arrow />}
				</div>
			</motion.button>

			{unavailable && (
				<div className="absolute top-0 right-0 max-w-[max-content] text-xs font-medium text-purple-500/70 ring ring-grey-100/70 px-2.5 py-1 rounded-bl-lg rounded-tr-lg cursor-not-allowed">
					{t('cardButton.comingSoon')}
				</div>
			)}
		</motion.div>
	)
}

function Arrow() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 16 16"
			id="Keyboard-Right-Arrow--Streamline-Nova"
			height={16}
			width={16}
		>
			<desc>Keyboard Right Arrow Streamline Icon: https://streamlinehq.com</desc>
			<path
				fill="currentColor"
				fillRule="evenodd"
				d="m10.6192 2.3430999999999997 5.185466666666667 5.185433333333333c0.2603333333333333 0.2603333333333333 0.2603333333333333 0.6824666666666667 0 0.9427999999999999l-5.185466666666667 5.185466666666667 -0.9427999999999999 -0.9427999999999999 4.047333333333333 -4.0474 -13.723733333333332 0v-1.3333333333333333l13.723733333333332 0 -4.047333333333333 -4.047359999999999 0.9427999999999999 -0.9428066666666666Z"
				clipRule="evenodd"
				strokeWidth={0.6667}
			/>
		</svg>
	)
}
